import { Observable } from 'rxjs/Observable'
import { combineEpics } from 'redux-observable'
import * as actions from './actions'
import * as selectors from './selectors'
import { showTransientNotification } from '@driftt/traffic-control'
import { api } from './api'
import { map, startWith, mergeMap, catchError } from 'rxjs/operators'

const fetchSettings = (action$, store) => {
  return action$
    .ofType(actions.fetchSettings.toString())
    .switchMap(() => {
      const orgId = store.getState().user.orgId
      return api
        .fetchSettings(orgId)
        .pipe(
          map(actions.fetchSettingsSuccess),
          startWith(actions.fetchSettingsPending()),
          catchError(() => Observable.of(actions.fetchSettingsFailed()))
        )
    })
}

const fetchBotConfig = (actions$, store) => {
  return actions$
    .ofType(actions.fetchBotConfig.toString())
    .switchMap(() => {
      const orgId = store.getState().user.orgId
      return api
        .fetchBotConfig(orgId)
        .pipe(
          map(actions.fetchBotConfigSuccess),
          startWith(actions.fetchBotConfigPending()),
          catchError(() => Observable.of(actions.fetchBotConfigFailed()))
        )
    })
}

const fetchSyncedKnowledgebaseList = (action$, store) => {
  return action$
    .ofType(actions.fetchSyncedKnowledgebaseList.toString())
    .switchMap(() => {
      const orgId = store.getState().user.orgId
      return api
        .fetchSyncedKnowledgeBaseList(orgId)
        .pipe(
          map(actions.fetchSyncedKnowledgebaseListSuccess),
          startWith(actions.fetchSyncedKnowledgebaseListPending()),
          catchError(() => Observable.of(actions.fetchSyncedKnowledgebaseListFailed()))
        )
  })
}

const setBotConfig = (action$, store) => {
  return action$
    .ofType(actions.setBotConfig.toString())
    .pluck('payload')
    .switchMap((payload) => {
      const orgId = store.getState().user.orgId
      return api
        .setBotConfig(orgId, payload)
        .pipe(
          map(actions.setBotConfigSuccess),
          startWith(actions.setBotConfigPending()),
          catchError(() => Observable.of(actions.setBotConfigFailed()))
        )
  })
}

const updateSettings = (action$, store) => {
  return action$
    .ofType(actions.updateSettings.toString())
    .switchMap(() => {
      return api
        .updateSettings(selectors.getSettings(store.getState()))
        .pipe(
          mergeMap((settings) => [
            actions.updateSettingsSuccess(settings),
            showTransientNotification({
              type: 'success',
              message: 'Successfully saved changes.'
            })
          ]),
          startWith(actions.updateSettingsPending()),
          catchError(() => Observable.of(
            actions.updateSettingsFailed(),
            showTransientNotification({
              type: 'error',
              message: 'Error saving your changes. Please reload the page and try again.'
            })
          ))
        )
    })
}

const fetchCustomLinks = action$ => {
  return action$
  .ofType(actions.fetchCustomLinks.toString())
  .switchMap(() => {
    return api
    .fetchCustomLinks()
    .pipe(
      map(actions.fetchCustomLinksSuccess),
      startWith(actions.fetchCustomLinksPending()),
      catchError(() => Observable.of(actions.fetchCustomLinksFailed()))
    )
  })
}

const editCustomLink = (action$, store) => {
  return action$
    .ofType(actions.editCustomLink.toString())
    .switchMap(() => {
      return api
        .editCustomLink(selectors.getCustomLink(store.getState()))
        .pipe(
          mergeMap(customLinks => [
            actions.editCustomLinkSuccess(customLinks),
            showTransientNotification({
              type: 'success',
              message: 'Successfully edited your custom link'
            })
          ]),
          startWith(actions.editCustomLinkPending()),
          catchError(() => Observable.of(
            actions.editCustomLinkFailed(),
            showTransientNotification({
              type: 'error',
              message: 'There was an error. Please try again.'
            })
          ))
        )
  })
}

const saveCustomLink = (action$, store) => {
  return action$
    .ofType(actions.saveCustomLink.toString())
    .switchMap(() => {
      return api
        .saveCustomLink(selectors.getCustomLink(store.getState()))
        .pipe(
          mergeMap(customLinks => [
            actions.saveCustomLinkSuccess(customLinks),
            showTransientNotification({
              type: 'success',
              message: 'Successfully added your custom link'
            })
          ]),
          startWith(actions.saveCustomLinkPending()),
          catchError(() => Observable.of(
            actions.saveCustomLinkFailed(),
            showTransientNotification({
              type: 'error',
              message: 'Error adding a custom link. Please try again.'
            })
          ))
        )
  })
}

const deleteCustomLink = (action$, store) => {
  return action$
    .ofType(actions.deleteCustomLink.toString())
    .pluck('payload')
    .switchMap(id => {
      return api
        .deleteCustomLink(id)
        .pipe(
          mergeMap(customLinks => [
            actions.deleteCustomLinkSuccess(customLinks)
          ]),
          startWith(actions.deleteCustomLinkPending()),
          catchError(() => Observable.of(
            actions.deleteCustomLinkFailed(),
            showTransientNotification({
              type: 'error',
              message: 'Error deleting your custom link. Please try again.'
          })
        )
    ))
  })
}

export const epic = combineEpics(
  fetchSettings,
  updateSettings,
  fetchBotConfig,
  fetchSyncedKnowledgebaseList,
  setBotConfig,
  saveCustomLink,
  fetchCustomLinks,
  editCustomLink,
  deleteCustomLink
)
